import _ from 'lodash';
import moment from 'moment';
import { useCallback, useRef, useState } from 'react';
import {
  fetchHolterEvents,
  calculateStartStopStripMomentOfListEvent,
  calculateStartStopStripMomentOfListEventV2,
  formatEventStrip,
} from '@/entities/event';
import { ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { eventTypeConvert } from '@/shared/lib/aiUtils';
import { useDeepCompareEffect, useUpdateEffect } from '@/shared/lib/customHooks';
import {
  selectAiChannel,
  selectChartHrsData,
  selectSelectedColumnInfo,
  selectSelectedDateValue,
  selectSelectedEventIndex,
  useReportStore,
} from '@/entities/report';
import { JUMP_DATE_STATUS } from '@/shared/model';
import { checkAndProcessBeatData, countBeatChannels } from '@/entities/beat';
import { checkAndProcessEcgData, formatDrawDisplay } from '@/entities/ecg';
import { calculateMinutelyHr } from '@/entities/heart-rate';
import { logError } from '@/shared/lib/apiHelpers';
import { isRangesOverlap } from '@/shared/lib/timeRange';
import type { EcgViewerWrapperProps } from './index';

/**
 * Backs the ECG viewer under the disclosure chart, loading whichever minute the user picked on the chart. It fetches that hour's rhythm events alongside the ECG and beats for the computed strip window, keeps only events overlapping the selected minute, trims samples and beats to the final strip range, records the AI beat channel count, and disables the previous or next button when the neighbouring minute has no average heart rate.
 *
 * @param props - ECG viewer wrapper props with study and profile ids, the ECG data map and the arrow click handler.
 * @returns An object with the loading flag, viewer ECG object, strip start epoch ref, AI channel count, both arrow disabled flags and the arrow click handler.
 */
const useEcgViewerWrapper = ({
  ecgDataMap,
  profileId,
  studyId,
  handleClickArrowButton,
}: Pick<
  EcgViewerWrapperProps,
  'ecgDataMap' | 'profileId' | 'studyId' | 'handleClickArrowButton'
>) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const setAiChannelAction = useReportStore((s) => s.actions.setAiChannel);
  /**
   * Writes the AI beat channel count for report tab 3 into the shared report store. It calls the store action with the tab's key bound in front of the caller's value.
   *
   * @param v - New value or updater for the AI channel count.
   * @returns Nothing.
   */
  const setAiChannel = (v: Parameters<typeof setAiChannelAction>[1]) =>
    setAiChannelAction(keyRecoil, v);
  /**
   * Reads the current minute-by-minute heart-rate series for report tab 3 outside of React. It pulls the value straight off the report store state instead of subscribing to it.
   *
   * @returns The heart-rate chart rows for the selected day.
   */
  const getChartHrsData = () => selectChartHrsData(keyRecoil)(useReportStore.getState());
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);

  const [isLoading, setIsLoading] = useState(true);
  const [ecgObject, setEcgObject] = useState<any>({});
  const [isDisablePrevBtn, setIsDisablePrevBtn] = useState(false);
  const [isDisableNextBtn, setIsDisableNextBtn] = useState(false);

  const startStripEpoch = useRef<any>(null);
  const stopStripEpoch = useRef<any>(null);
  const isJumpToEnd = useRef(false);
  const isJumpToStart = useRef(false);
  const currentQueryEventHour = useRef(null);

  let isActiveQuery: boolean;

  /**
   * Converts a rhythm event into the highlight band the ECG viewer draws over the strip. It maps the event start and stop to sample positions clamped to the loaded window, colours artifact grey, AFib orange and everything else red, computes the jump offset in samples, and labels only red events with their display type; minute rows without a start and stop yield a zero-width band.
   *
   * @param event - Rhythm event or minute row to place.
   * @param eventJumpOffset - Epoch the viewer should scroll to.
   * @param isSelected - Whether this is the event the viewer opened on.
   * @returns The viewer highlight descriptor with sample positions, colour, jump offset and type label.
   */
  const getEventInfoInEcgViewer = ({ event, eventJumpOffset = 0, isSelected = false }: any) => {
    const { samplingFrequency } = ecgDataMap;
    const isEvent = event.start && event.stop; // not minute info (minute info dont have start and stop)
    let startPos = 0;
    let stopPos = 0;
    let color;
    const jumpOffset = eventJumpOffset
      ? Math.round(((eventJumpOffset - startStripEpoch.current) / 1000) * samplingFrequency)
      : 0;
    if (isEvent) {
      const isArtifactEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      color = isArtifactEvent ? 'gray' : isAfibEvent ? 'orange' : 'red';
      const startEpoch = new Date(event.start).getTime();
      const stopEpoch = new Date(event.stop).getTime();
      if (startStripEpoch.current <= startEpoch) {
        const eventDiffSeconds = Math.abs(startEpoch - startStripEpoch.current) / 1000;
        startPos = Math.round(eventDiffSeconds * samplingFrequency);
      }
      if (stopEpoch >= stopStripEpoch.current) {
        stopPos = (stopStripEpoch.current - startStripEpoch.current) / 1000;
      } else {
        stopPos = (stopEpoch - startStripEpoch.current) / 1000;
      }
      stopPos = Math.round(stopPos * samplingFrequency);
    }
    return {
      startPos,
      stopPos,
      jumpOffset,
      color,
      id: event.id,
      isSelected,
      type: isEvent && color === 'red' ? (eventTypeConvert as any)[event.type] : '', // Show event type text in ecg viewer for others event
    } as any;
  };

  /**
   * Builds the viewer descriptor for the ECG disclosure strip and its highlighted events. It maps every event to a highlight band, prepends a computed max or min heart-rate band when the jump asked for one, formats the selected event's draw widgets, and sets the ECG object with the AI beat prediction and the shared viewer config's channels, filters, speed, gain and window, jumping to the strip start or end when an arrow triggered the load.
   *
   * @param ecg - Decoded ECG channel samples.
   * @param beats - AI beat sample positions.
   * @param hesBeatStatus - Per-beat classification statuses.
   * @param events - Events overlapping the window, the last entry being the minute row.
   * @param eventIndex - Index of the event the viewer should open on.
   * @returns Nothing.
   */
  const createObjectEcg = (
    ecg: any,
    beats: any,
    hesBeatStatus: any,
    events: any[],
    eventIndex: any,
  ) => {
    const { channels, gain, samplingFrequency } = ecgDataMap;
    let isJumpMaxHr;
    let isJumpMinHr;
    const eventsInfo = _.map(events, (event, index) => {
      if (event.jumpStatus === JUMP_DATE_STATUS.MAXHR) {
        isJumpMaxHr = true;
      } else if (event.jumpStatus === JUMP_DATE_STATUS.MINHR) {
        isJumpMinHr = true;
      }
      return getEventInfoInEcgViewer({
        event,
        eventJumpOffset: event.jumpOffset,
        isSelected: index === eventIndex,
      });
    });
    if (isJumpMaxHr || isJumpMinHr) {
      const { maxHrPosRange, minHrPosRange } = calculateMinutelyHr(
        beats,
        hesBeatStatus,
        samplingFrequency,
      );
      if (isJumpMaxHr && !_.isEmpty(maxHrPosRange)) {
        eventsInfo.unshift({
          startPos: maxHrPosRange[0],
          stopPos: maxHrPosRange[1],
          jumpOffset: 0,
          color: 'red',
          id: 'maxHr',
          isSelected: true,
        });
      } else if (isJumpMinHr && !_.isEmpty(minHrPosRange)) {
        eventsInfo.unshift({
          startPos: minHrPosRange[0],
          stopPos: minHrPosRange[1],
          jumpOffset: 0,
          color: 'red',
          id: 'minHr',
          isSelected: true,
        });
      }
    }
    const mainEvent = events[eventIndex];
    // *: Add capture sample mark
    const { draw } = formatDrawDisplay(
      mainEvent.draw,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );

    const viewerObject = {
      options: {},
      widgets: draw,
    };
    const endStripPos =
      ((stopStripEpoch.current - startStripEpoch.current) / 1000) * samplingFrequency;
    setEcgObject({
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      viewerObject,
      diagnosticLead: 2,
      samplingRate: samplingFrequency,
      events: eventsInfo,
      aiPredict: [
        {
          beats,
          hesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(hesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      jumpOffset: isJumpToStart.current
        ? 1
        : isJumpToEnd.current
          ? endStripPos
          : eventsInfo[0].jumpOffset,
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
    });
  };

  /**
   * Handles the previous and next minute buttons above the ECG viewer. It records whether the next window should be jumped to its start or its end, then forwards the request to the parent handler.
   *
   * @param isNext - True when moving to the following minute.
   * @returns Nothing.
   */
  const clickArrowButtonHandle = useCallback((isNext: any) => {
    if (isNext) {
      isJumpToStart.current = true;
    } else {
      isJumpToEnd.current = true;
    }
    handleClickArrowButton(isNext);
  }, []);

  /**
   * Loads everything the ECG viewer needs for one selected minute of the disclosure chart. It fetches the hour's rhythm events plus the ECG and beats for the default strip window in parallel, appends the events overlapping that minute, recomputes the strip range from the merged set and trims samples and beat positions to it, sets the AI channel count, builds the viewer object and then decides whether the previous and next buttons stay enabled.
   *
   * @param selectedEvents - Events and the minute row selected on the chart, the minute row last.
   * @param eventIndex - Index of the event the viewer should open on.
   * @returns A promise resolving once the viewer object and button states are set, or the failure has been handled.
   */
  const downloadDataEcgV2 = async (selectedEvents: any[], eventIndex = 0) => {
    if (_.isEmpty(selectedEvents)) {
      return;
    }
    setIsLoading(true);
    try {
      const events = [...selectedEvents];
      const minuteInfo = events[events.length - 1];
      const startHours = moment(minuteInfo.startTimeEpoch).startOf('hours');
      const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListEventV2({
        startTimeEpoch: minuteInfo.startTimeEpoch,
      });
      const startSearchDate = startHours.toISOString();
      currentQueryEventHour.current = startSearchDate;
      const promise = [
        fetchHolterEvents(
          {
            studyId,
            profileId,
            types: [
              TYPE_RHYTHM_EVENT_ENUM.TACHY,
              TYPE_RHYTHM_EVENT_ENUM.BRADY,
              TYPE_RHYTHM_EVENT_ENUM.PAUSE,
              TYPE_RHYTHM_EVENT_ENUM.MAX_HR,
              TYPE_RHYTHM_EVENT_ENUM.MIN_HR,
              TYPE_RHYTHM_EVENT_ENUM.VT,
              TYPE_RHYTHM_EVENT_ENUM.SVT,
              TYPE_RHYTHM_EVENT_ENUM.AVB2,
              TYPE_RHYTHM_EVENT_ENUM.AVB3,
            ],
            startSearchDate,
            stopSearchDate: startHours.clone().add(1, 'hours').toISOString(),
          },
          0,
          true,
          null,
          false,
        ),
        checkAndProcessEcgData({
          ecgDataMap,
          dataLength:
            ((defaultStopStrip.valueOf() - defaultStartStrip.valueOf()) / 1000) *
            ecgDataMap.samplingFrequency,
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          },
        } as any),
        checkAndProcessBeatData({
          ecgDataMap,
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          },
        } as any),
      ];
      const [{ events: downloadEvents }, ecgData, holterBeats]: any[] = await Promise.all(promise);
      if (isActiveQuery) {
        const startMinuteString = moment(minuteInfo.startTimeEpoch).toISOString();
        const stopMinuteString = moment(minuteInfo.startTimeEpoch).add(60, 'seconds').toISOString();
        _.forEach(formatEventStrip(downloadEvents), (downloadEvent) => {
          if (
            isRangesOverlap(
              downloadEvent.start as any,
              downloadEvent.stop as any,
              startMinuteString as any,
              stopMinuteString as any,
            )
          ) {
            downloadEvent.startTimeEpoch = minuteInfo.startTimeEpoch;
            events.push(downloadEvent);
          }
        });
        const { startStrip, stopStrip } = calculateStartStopStripMomentOfListEvent({
          events,
        });
        startStripEpoch.current = startStrip.valueOf();
        stopStripEpoch.current = stopStrip.valueOf();
        // * Cut ecgdata and beat in range
        const cuttedMsStart = startStripEpoch.current - defaultStartStrip.valueOf();
        const cuttedMsStop = defaultStopStrip.valueOf() - stopStripEpoch.current;
        const cuttedSampleStart = (cuttedMsStart / 1000) * ecgDataMap.samplingFrequency;
        const cuttedSampleStop = (cuttedMsStop / 1000) * ecgDataMap.samplingFrequency;
        const cuttedEcgData = _.map(ecgData, (subArr: any) =>
          subArr.slice(cuttedSampleStart, subArr.length - cuttedSampleStop),
        );
        const cuttedBeatSample: any[] = [];
        const cuttedHesBeatStatus: any[] = [];
        const cuttedBeatChannel: any[] = [];
        if (!_.isEmpty(holterBeats?.beats)) {
          _.forEach(holterBeats?.beats, (sample, index) => {
            const epochBeat =
              defaultStartStrip.valueOf() + (sample * 1000) / ecgDataMap.samplingFrequency;
            if (epochBeat >= startStripEpoch.current && epochBeat <= stopStripEpoch.current) {
              cuttedHesBeatStatus.push(holterBeats?.hesBeatStatus[index]);
              cuttedBeatChannel.push(holterBeats?.beatChannels[index]);
              cuttedBeatSample.push(
                Math.round(
                  ((epochBeat - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
                ),
              );
            }
          });
        }

        const cuttedHolterBeats = {
          beats: cuttedBeatSample,
          hesBeatStatus: cuttedHesBeatStatus,
          beatChannels: cuttedBeatChannel,
        };
        setAiChannel(countBeatChannels(cuttedHolterBeats?.beatChannels) as any); // pre-existing: countBeatChannels returns number, aiChannelState is typed 1|2|3
        createObjectEcg(
          cuttedEcgData,
          cuttedHolterBeats?.beats,
          cuttedHolterBeats?.hesBeatStatus,
          events,
          eventIndex,
        );
        const hrsData = getChartHrsData();
        const prevMinute = minuteInfo.startTimeEpoch - 60000;
        const nextMinute = minuteInfo.startTimeEpoch + 60000;
        const prevMinuteHrData = _.find(hrsData, (x) => x.id === prevMinute);
        const nextMinuteHrData = _.find(hrsData, (x) => x.id === nextMinute);
        setIsDisablePrevBtn(!prevMinuteHrData?.avgHr);
        setIsDisableNextBtn(!nextMinuteHrData?.avgHr);
        setIsLoading(false);
      }
      isJumpToEnd.current = false;
      isJumpToStart.current = false;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }
      setIsLoading(false);
    }
  };

  useDeepCompareEffect(
    () => {
      isActiveQuery = true;
      downloadDataEcgV2(selectedColumnInfo, selectedEventIndex);
    },
    [selectedColumnInfo, selectedEventIndex, isRenderViewerEcg],
    () => {
      isActiveQuery = false;
    },
  );

  useUpdateEffect(() => {
    setIsLoading(true);
  }, [selectedDateValue]);

  return {
    isLoading,
    ecgObject,
    startStripEpoch,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    clickArrowButtonHandle,
  };
};

export default useEcgViewerWrapper;
